import { Col, Container, Row } from "react-bootstrap";
import React, { ReactNode, useEffect, useState } from "react";
import CustomButton from "shared/components/button/CustomButton";
import CustomModal from "shared/components/modal/CustomModal";
import { CustomDatePicker } from "shared/components/date/CustomDatePicker";
import { CustomTimePicker } from "shared/components/date/CustomTimePicker";
import CustomSelect from "shared/components/select/CustomSelect";
import { TipoSacado, getTipoSacadoFormatado } from "constants/EnumTipoSacado";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { AgendarContatoSchema } from "shared/yup/schemas/AgendarContatoSchema";
import { SelectOptionType } from "types/select/SelectOptionType";
import SelectAsync from "shared/components/select/SelectAsync";
import { api } from "constants/RotasAPI";
import { RegistroCondominioDTO } from "types/dto/RegistroCondominioDTO";
import { RegistroUnidadeDTO } from "types/dto/RegistroUnidadeDTO";
import { RegistroUsuarioDTO } from "types/dto/RegistroUsuarioDTO";
import { AgendamentoContatoDTO } from "types/dto/AgendamentoContatoDTO";
import {
  criarDataByLocalDate,
  criarDataByLocalDateAndLocalTimeString,
  formatarDateParaLocalTime,
  getDataFormatadaParaVisualizacao,
  getHorarioFormatadoParaVisualizacao,
} from "shared/util/functions/formatacoesDeValores/FormatarValores";
import { usePostAgendarContato } from "hooks/agendamentoContato/usePostAgendarContatos";
import { toast } from "react-hot-toast";
import showAlert from "shared/util/functions/alert/showAlert";
import { AgendamentoContatoEdicaoVisualizacaoDTO } from "types/dto/AgendamentoContatoEdicaoVisualizacaoDTO";
import { criarSelectOptions } from "shared/util/functions/selectOptions/SelectOptionsFunctions";
import VisualizacaoInput from "shared/components/input/VisualizacaoInput";
import { BsFillPencilFill } from "react-icons/bs";
import { useGetAgendamentoContatoEdicaoVisualizacao } from "hooks/agendamentoContato/useGetAgendamentoContatoEdicaoVisualizacao";
import { queryClient } from "shared/util/queryClient";
import { useAgendamentoContatoContext } from "context/gestaoCobranca/AgendamentoContatoContext";

interface ModalEdicaoVisualizacaoAgendarContatoProps {
  agendamentoId: number;
  agendamentoUuid: string;
  isVisualizacao: boolean;
  showModal: boolean;
  onCloseModal: () => void;
}

interface AgendamentoContatoForm {
  condominio: SelectOptionType<RegistroCondominioDTO> | null;
  unidade: SelectOptionType<RegistroUnidadeDTO> | null;
  responsavelAgendamento: SelectOptionType<RegistroUsuarioDTO> | null;
  dataAgendamento: Date | null;
  horarioAgendamento: Date;
  sacado: SelectOptionType<TipoSacado> | undefined;
}

const ModalEdicaoVisualizacaoAgendarContato: React.FC<
  ModalEdicaoVisualizacaoAgendarContatoProps
> = (props) => {
  const [isVisualizacao, setIsVisualizacao] = useState<boolean>();

  const { register, handleSubmit, reset, control, formState, watch, setValue } =
    useForm<AgendamentoContatoForm>({
      resolver: yupResolver<AgendamentoContatoForm>(AgendarContatoSchema),
    });
  const { possuiPermissaoEditarAgendamento } = useAgendamentoContatoContext();

  const { data, isError } = useGetAgendamentoContatoEdicaoVisualizacao({
    agendamentoId: props.agendamentoId.toString(),
  });

  useEffect(() => {
    if (isError) {
      queryClient.removeQueries("agendamento-contato-edicao-visualizacao");
    } else if (props.showModal) {
      setarDefaultValues();
      setIsVisualizacao(props.isVisualizacao);
    }
  }, [data, props.showModal]);

  const setarDefaultValues = () => {
    if (data) {
      const agendamento = data as AgendamentoContatoEdicaoVisualizacaoDTO;
      setValue("dataAgendamento", criarDataByLocalDate(agendamento.dataAgendamento));
      setValue(
        "horarioAgendamento",
        criarDataByLocalDateAndLocalTimeString(
          agendamento.dataAgendamento,
          agendamento.horarioAgendamento
        )
      );
      setValue(
        "sacado",
        criarSelectOptions(agendamento.tipoSacado, getTipoSacadoFormatado(agendamento.tipoSacado))
      );
      setValue(
        "condominio",
        criarSelectOptions(agendamento.condominio.id, agendamento.condominio.nomeFantasia)
      );
      setValue(
        "unidade",
        criarSelectOptions(agendamento.unidade.id, agendamento.unidade.blocoNumero)
      );
      setValue(
        "responsavelAgendamento",
        criarSelectOptions(agendamento.nomeEmailResponsavel, agendamento.nomeEmailResponsavel.nome)
      );
    }
  };

  const { mutateAsync: postAgendarContato } = usePostAgendarContato();

  const opcoesTipoSacado: SelectOptionType<TipoSacado>[] = [
    {
      label: "Proprietário",
      value: TipoSacado.PROPRIETARIO,
    },
    {
      label: "Inquilino",
      value: TipoSacado.INQUILINO,
    },
  ];

  const renderFooterModalAdicionarContato = (): ReactNode => {
    if (isVisualizacao) {
      return (
        <Row>
          <Col className="d-flex justify-content-end p-0">
            <CustomButton
              id="btn-voltar-modal-agendar-contato"
              label="Voltar"
              onClick={() => closeModal()}
              variant="warning"
              className="me-2"
            />
          </Col>
        </Row>
      );
    }
    return (
      <Row>
        <Col className="d-flex justify-content-end p-0">
          <CustomButton
            id="btn-cancelar-modal-agendar-contato"
            label="Cancelar"
            onClick={() => closeModal()}
            variant="danger-alt"
            className="me-2"
          />
          <CustomButton
            id="btn-cancelar-modal-agendar-contato"
            label="Salvar"
            onClick={handleSubmit(agendarContato)}
            variant="primary"
          />
        </Col>
      </Row>
    );
  };

  const agendarContato = async (form: AgendamentoContatoForm) => {
    const agendamentoContatoDTO = {
      id: props.agendamentoId,
      uuid: props.agendamentoUuid,
      dataAgendamento: form.dataAgendamento?.toISOString(),
      horarioAgendamento: formatarDateParaLocalTime(form.horarioAgendamento),
      unidadeId: form.unidade?.value,
      tipoSacado: form.sacado?.value,
      nomeResponsavel: form.responsavelAgendamento?.value?.nome,
      emailResponsavel: form.responsavelAgendamento?.value?.email,
    } as AgendamentoContatoDTO;

    if (formState.isDirty) {
      try {
        await postAgendarContato(agendamentoContatoDTO);
        reset();
        props.onCloseModal();
      } catch (error: any) {
        if (error?.response?.data?.validation[0]?.message) {
          toast.error(error.response.data.validation[0].message);
        } else {
          toast.error("Erro ao salvar registro.");
        }
      }
    } else {
      closeModal();
    }
  };

  const renderBodyModalEdicaoVisualizacaoAgendarContato = (): ReactNode => {
    return (
      <Container>
        <Row className="mb-3">{renderHeaderBody()}</Row>
        <Row className="mb-3">
          {renderCondominio()}
          {renderUnidade()}
        </Row>
        <Row className="mb-3">
          {renderInputSacado()}
          {renderResponsavelAgendamento()}
        </Row>
        <Row className="mb-3">
          {renderDataAgendamento()}
          {renderHorarioAgendamento()}
        </Row>
      </Container>
    );
  };
  const renderHeaderBody = () => {
    return (
      <Col className="d-flex justify-content-between">
        <div>Registre aqui o agendamento de contato com a unidade selecionada</div>
        {isVisualizacao && possuiPermissaoEditarAgendamento && (
          <span onClick={() => setIsVisualizacao(false)} className="blue-label-gc">
            <BsFillPencilFill className="cursor-pointer me-2 color-inherit" size={15} />
            Editar agendamento
          </span>
        )}
      </Col>
    );
  };

  const renderCondominio = (): ReactNode => {
    if (isVisualizacao) {
      return (
        <Col xl={5} sm={5} xs={5}>
          <VisualizacaoInput id="condominio" label="Condomínio" value={watch("condominio.label")} />
        </Col>
      );
    }

    return (
      <Col xl={5} sm={5} xs={5}>
        <SelectAsync
          usarValidacaoParaFormulario
          id="selecionar-condominio"
          name="condominio"
          label="Condomínio"
          endpoint={api.condominio.getRegistrosCondominios}
          onChange={(opcaoSelecionada) => {
            if (watch("unidade")) {
              setValue("unidade", null);
            }
          }}
          control={control}
          register={register}
          setValue={setValue}
          nomeAtributoValue="id"
          nomeAtributoLabel="nomeFantasia"
        />
      </Col>
    );
  };

  const renderUnidade = (): ReactNode => {
    if (isVisualizacao) {
      return (
        <Col xl={5} sm={5} xs={5}>
          <VisualizacaoInput id="unidade" label="Unidade" value={watch("unidade.label")} />
        </Col>
      );
    }
    return (
      <Col xl={5} sm={5} xs={5}>
        <SelectAsync
          usarValidacaoParaFormulario
          id="unidade"
          name="unidade"
          label="Unidade"
          nomeAtributoValue="id"
          nomeAtributoLabel="blocoNumero"
          endpoint={`${api.unidade.getRegistrosUnidades}/${watch("condominio")?.value}`}
          atributosRefresh={[watch("condominio")?.value]}
          isDisabled={!watch("condominio")?.value}
          control={control}
          register={register}
          setValue={setValue}
        />
      </Col>
    );
  };

  const renderInputSacado = (): ReactNode => {
    if (isVisualizacao) {
      return (
        <Col xl={5} sm={5} xs={5}>
          <VisualizacaoInput id="sacado" label="Sacado" value={watch("sacado.label")} />
        </Col>
      );
    }
    return (
      <Col xl={5} sm={5} xs={5}>
        <CustomSelect
          id="tipo-sacado"
          label="Sacado"
          usarValidacaoParaFormulario
          name="sacado"
          register={register}
          control={control}
          options={opcoesTipoSacado}
          setValue={setValue}
        />
      </Col>
    );
  };

  const renderResponsavelAgendamento = (): ReactNode => {
    if (isVisualizacao) {
      return (
        <Col xl={5} sm={5} xs={5}>
          <VisualizacaoInput
            id="responsavel-agendamento"
            label="Responsável pelo agendamento"
            value={watch("responsavelAgendamento.label")}
          />
        </Col>
      );
    }
    return (
      <Col xl={5} sm={5} xs={5}>
        <SelectAsync
          usarValidacaoParaFormulario
          id="responsavel-agendamento"
          name="responsavelAgendamento"
          label="Responsável pelo agendamento"
          endpoint={`${api.usuario.getRegistrosUsuarios}`}
          control={control}
          setValue={setValue}
          register={register}
          nomeAtributoLabel="nomeUsuario"
          nomeAtributoValue="nomeEmailUsuario"
        />
      </Col>
    );
  };

  const renderDataAgendamento = (): ReactNode => {
    if (isVisualizacao) {
      return (
        <Col xl={5} sm={5} xs={5}>
          <VisualizacaoInput
            id="data-agendamento"
            label="Data do agendamento"
            value={getDataFormatadaParaVisualizacao(watch("dataAgendamento"))}
          />
        </Col>
      );
    }
    return (
      <Col xl={5} sm={5} xs={5}>
        <CustomDatePicker
          usarValidacaoParaFormulario
          name="dataAgendamento"
          control={control}
          register={register}
          setValue={setValue}
          label="Data do agendamento"
          popperDirection="right-start"
        />
      </Col>
    );
  };

  const renderHorarioAgendamento = (): ReactNode => {
    if (isVisualizacao) {
      return (
        <Col xl={5} sm={5} xs={5}>
          <VisualizacaoInput
            id="data-agendamento"
            label="Horário do agendamento"
            value={getHorarioFormatadoParaVisualizacao(watch("horarioAgendamento"))}
          />
        </Col>
      );
    }
    return (
      <Col xl={5} sm={5} xs={5}>
        <CustomTimePicker
          usarValidacaoParaFormulario
          label="Horário do agendamento"
          name="horarioAgendamento"
          control={control}
          register={register}
          setValue={setValue}
          popperDirection="right-start"
        />
      </Col>
    );
  };

  const closeModal = () => {
    const fecharModal = () => {
      reset();
      props.onCloseModal();
    };
    if (formState.isDirty) {
      showAlert({
        texto:
          "Existem alterações que não foram salvas na tela. Tem certeza de que deseja sair sem salvar?",
        tipoAlerta: "CONFIRMAR_CANCELAR_ALERT",
        onConfirm: () => fecharModal(),
      });
    } else {
      fecharModal();
    }
  };

  return (
    <CustomModal
      titulo="Agendamentos"
      onCloseModal={() => closeModal()}
      customFooter={renderFooterModalAdicionarContato()}
      showModal={props.showModal}
      size="lg"
      tipoModal="MODAL_LATERAL"
      fecharModalAoClicarFora={!formState.isDirty}
      scrollable
    >
      {renderBodyModalEdicaoVisualizacaoAgendarContato()}
    </CustomModal>
  );
};

export default ModalEdicaoVisualizacaoAgendarContato;
